<script setup lang="ts">
import type { Release } from '@/types'
import { useForm } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import {
  computeFileChecksums,
  requestDirectUpload,
  uploadFileBytes,
} from '@/lib/directUpload'
import { formatBytes } from '@/lib/format'
import { firstFormError } from '@/lib/forms'
import {
  adminReleaseArtifactsPath,
  directUploadsAdminReleaseArtifactsPath,
} from '@/routes'

const props = defineProps<{
  release: Pick<Release, 'id'>
}>()

const form = useForm<{
  blob_signed_id: string
  identifier: string
  description: string
}>({
  blob_signed_id: '',
  identifier: '',
  description: '',
})

const selectedFile = ref<File | null>(null)

// The browser uploads bytes straight to object storage before the Inertia
// create request runs, so the upload phase tracks its own state.
const uploading = ref(false)
const uploadError = ref<string>()
const progress = ref<{ loaded: number, total: number }>()

const processing = computed(() => uploading.value || form.processing)

// The create action can also surface server-side `file` and `checksum` errors
// that do not map to a literal form field, so they are read by key.
function serverError(key: string) {
  return firstFormError((form.errors as Record<string, string[]>)[key])
}

const fileError = computed(() => uploadError.value ?? serverError('file'))
const blobError = computed(() => firstFormError(form.errors.blob_signed_id))
const baseError = computed(() => serverError('checksum') ?? serverError('base'))

function resetUploadState() {
  uploading.value = false
  uploadError.value = undefined
  progress.value = undefined
}

async function submit() {
  const file = selectedFile.value
  if (!file)
    return

  if (!form.identifier.trim()) {
    form.setError('identifier', ['Identifier is required'])
    return
  }

  resetUploadState()
  form.clearErrors()
  uploading.value = true

  try {
    const checksums = await computeFileChecksums(file)

    const { blob_signed_id, direct_upload } = await requestDirectUpload(
      directUploadsAdminReleaseArtifactsPath(props.release.id),
      {
        identifier: form.identifier,
        filename: file.name,
        checksum: checksums.sha256Hex,
        active_storage_checksum: checksums.activeStorageChecksum,
        byte_size: checksums.byteSize,
        content_type: checksums.contentType,
      },
    )

    await uploadFileBytes(direct_upload, file, (loaded, total) => {
      progress.value = { loaded, total }
    })

    form.blob_signed_id = blob_signed_id
  }
  catch (error) {
    uploadError.value = error instanceof Error ? error.message : 'Upload failed'
    uploading.value = false
    return
  }

  uploading.value = false

  form
    .transform(data => ({
      blob_signed_id: data.blob_signed_id,
      identifier: data.identifier,
      description: data.description || undefined,
    }))
    .post(adminReleaseArtifactsPath(props.release.id), {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        selectedFile.value = null
        resetUploadState()
      },
    })
}
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="submit">
    <UFormField
      label="File"
      name="file"
      description="The binary artifact (installer, zip, package) uploaded to storage."
      :error="fileError ?? blobError"
      required
    >
      <UFileUpload
        v-model="selectedFile"
        icon="i-lucide-upload"
        label="Drop a file here or click to browse"
        description="The upload checksum and size are stored after the direct upload."
        class="w-full"
      />
    </UFormField>

    <div class="grid gap-4 sm:grid-cols-2">
      <UFormField
        label="Identifier"
        name="identifier"
        description="Abstract label the installer maps to a file, e.g. binary, installer, config-template."
        :error="firstFormError(form.errors.identifier)"
        required
      >
        <UInput
          v-model="form.identifier"
          placeholder="binary"
          icon="i-lucide-tag"
          class="w-full font-mono"
        />
      </UFormField>

      <UFormField
        label="Description"
        name="description"
        description="Optional human-readable note."
        :error="firstFormError(form.errors.description)"
      >
        <UInput
          v-model="form.description"
          placeholder="Main executable"
          class="w-full"
        />
      </UFormField>
    </div>

    <UAlert
      v-if="baseError"
      color="error"
      variant="subtle"
      icon="i-lucide-circle-alert"
      :description="baseError"
    />

    <div v-if="progress" class="flex items-center gap-3">
      <UProgress
        :model-value="progress.total ? (progress.loaded / progress.total) * 100 : 0"
        class="flex-1"
      />
      <span class="text-muted text-xs tabular-nums">
        {{ formatBytes(progress.loaded) }} / {{ formatBytes(progress.total) }}
      </span>
    </div>

    <div class="flex justify-end">
      <UButton
        type="submit"
        label="Upload artifact"
        icon="i-lucide-upload"
        :loading="processing"
        :disabled="processing || !selectedFile"
      />
    </div>
  </form>
</template>
